import type {RevaValueParameterType} from "@reva/shared/reva-spec";
import type {ValuationData} from "@reva/shared/valuation-data";

import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {ValidationErrorI18n} from "../validation";
import {WebError} from "../web-error";

/**
 * Parameter types that can be persisted via the parameter/property API.
 * Excludes the read-only types META and FIRST_FINALIZE, which the backend cannot store.
 */
export type PersistableParameterType = Exclude<RevaValueParameterType, "META" | "FIRST_FINALIZE">;

/** A single parameter entry in a multi-parameter persistence request. */
export interface PersistParameterEntry {
    parameter: string;
    parameterValue: unknown;
    comment?: string;
}

/** A single document-property entry in a multi-property persistence request. */
export interface PersistDocumentPropertyEntry {
    property: string;
    propertyValue: unknown;
}

/**
 * Maps each persistable parameter type to the final path segment appended after the valuation id.
 * AVM uses the bare valuation path (empty segment). The total {@link Record} guarantees at compile
 * time that every persistable type has a mapping.
 */
const PARAMETER_TYPE_TO_PATH: Record<PersistableParameterType, string> = {
    AVM: "",
    DOC_PROPERTY: RevaApi.PATH_VALUATION_PROPERTIES,
    ENERGY_CERT: RevaApi.PATH_VALUATION_ENERGY_CERTIFICATE,
    IDP_ENERGY_CERTIFICATE: RevaApi.PATH_VALUATION_IDP_ENERGY_CERTIFICATE,
    REGISTER: RevaApi.PATH_VALUATION_REGISTER,
    INSPECTION_DERIVED: RevaApi.PATH_VALUATION_INSPECTION_DERIVED,
    PLOT: RevaApi.PATH_VALUATION_PLOT,
    IMMODROP: RevaApi.PATH_VALUATION_IMMODROP,
};

/**
 * Common base for persisting parameters/properties of a valuation via a PATCH request. Owns the
 * shared parameter-type → URL selection, request execution and (422) response handling. Subclasses
 * provide the request payload (single entry vs. multiple entries) and a debug description.
 */
export abstract class AbstractPersistApiInterface extends SimpleApiInterface<ValuationData> {
    protected constructor(
        protected readonly parameterType: PersistableParameterType,
        protected readonly id: string | null,
    ) {
        super();
    }

    /** Human-readable description of the persisted data, used only for debug logging. */
    protected abstract describePersistedData(): string;

    protected validate(): void {
        if (!this.id) {
            console.error(
                `Trying to persist ${this.describePersistedData()} (of type ${this.parameterType}) without a RevaId!`,
            );
            throw new ValidationErrorI18n("id", "general.error.unspecific");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        const segment = PARAMETER_TYPE_TO_PATH[this.parameterType];
        // AVM has an empty segment; filter it out so the URL stays /valuations/{id}.
        const pathSegments = [RevaApi.PATH_VALUATIONS, this.id!, segment].filter(Boolean);
        return api.buildRevaApiUrl(pathSegments, this.buildQueryParams());
    }

    /** Query parameters for the request URL. Override to add type-specific parameters. */
    protected buildQueryParams(): Record<string, any> {
        return {};
    }

    protected abstract buildPayload(): object;

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPATCH(url, credentials, this.buildPayload());
    }

    protected handleApiErrors(e: Error): ValuationData {
        // Persistence-specific 422 handling: surface the "missing comment" prompt (R500) so the caller
        // can ask for a comment without resetting state; map any other 422 to the generic message.
        if (e instanceof WebError && e.status === 422) {
            if (e.getErrorCode() === "R500" && e.json?.param) {
                throw new ErrorI18n("object.error.missingComment", {param: e.json.param});
            }
            throw new ErrorI18n("object.error.422");
        }
        return super.handleApiErrors(e);
    }
}

/* -------------------------------- Single entry -------------------------------- */

/**
 * Abstract base for persisting a single entry (parameter or document property) of a valuation.
 * Adds the entry key/value and the key validation; subclasses provide the payload shape.
 */
export abstract class AbstractPersistSingleEntryApiInterface extends AbstractPersistApiInterface {
    protected constructor(
        parameterType: PersistableParameterType,
        id: string | null,
        protected readonly key: string,
        protected readonly value: unknown,
    ) {
        super(parameterType, id);
    }

    protected abstract describePersistedKey(): string;

    protected override describePersistedData(): string {
        return `${this.describePersistedKey()} ${this.key}`;
    }

    protected override validate(): void {
        super.validate();
        if (!this.key) {
            console.error(
                `Trying to persist a ${this.describePersistedKey()} (of type ${this.parameterType}), but ${this.describePersistedKey()} name is empty!`,
            );
            throw new ValidationErrorI18n("key", "general.error.unspecific");
        }
    }
}

/**
 * Persists a single value parameter of a valuation (PATCH {@link RevaApi.PATH_VALUATIONS}/{id}[/segment]).
 * Supports an optional audit comment. DOC_PROPERTY is excluded — it uses a different payload format
 * and is handled by {@link PersistSingleDocumentPropertyApiInterface}.
 */
export class PersistSingleParameterApiInterface extends AbstractPersistSingleEntryApiInterface {
    constructor(
        parameterType: Exclude<PersistableParameterType, "DOC_PROPERTY">,
        id: string | null,
        parameter: string,
        value: unknown,
        private readonly comment?: string | null,
    ) {
        super(parameterType, id, parameter, value);
    }

    protected override describePersistedKey(): string {
        return "parameter";
    }

    protected buildPayload(): object {
        return {
            parameter: this.key,
            parameterValue: this.value,
            ...(this.comment !== undefined && {comment: this.comment}),
        };
    }
}

/**
 * Persists a single document property of a valuation (PATCH {@link RevaApi.PATH_VALUATIONS}/{id}/properties).
 * Supports a `technical` flag which appends `?technical=true` for internal (non-user) properties.
 * When a snapshot is loaded, `snapshotNumber` appends `?snapshot=<n>` so the backend frames the response
 * as that snapshot instead of switching the client to the live state.
 */
export class PersistSingleDocumentPropertyApiInterface extends AbstractPersistSingleEntryApiInterface {
    constructor(
        id: string | null,
        property: string,
        value: unknown,
        private readonly technical: boolean = false,
        private readonly snapshotNumber: number | null = null,
    ) {
        super("DOC_PROPERTY", id, property, value);
    }

    protected override describePersistedKey(): string {
        return "property";
    }

    protected buildPayload(): object {
        return {
            property: this.key,
            propertyValue: this.value,
        };
    }

    protected override buildQueryParams(): Record<string, any> {
        return {
            technical: this.technical ? "true" : null,
            snapshot: this.snapshotNumber,
        };
    }

    /**
     * Skip the success log for technical properties: they store valuation settings/preferences
     * rather than actual valuation data.
     */
    protected override logSuccessfulResponse(url: string): void {
        if (this.technical) {
            return;
        }
        super.logSuccessfulResponse(url);
    }
}

/* ------------------------------- Multiple entries ------------------------------- */

/**
 * Abstract base for persisting multiple entries of the same type in one request. The pre-built
 * entries are wrapped into a `{ values: [...] }` payload.
 */
export abstract class AbstractPersistMultipleEntriesApiInterface extends AbstractPersistApiInterface {
    protected constructor(
        parameterType: PersistableParameterType,
        id: string | null,
        protected readonly entries: ReadonlyArray<PersistParameterEntry | PersistDocumentPropertyEntry>,
    ) {
        super(parameterType, id);
    }

    protected abstract describePersistedKeys(): string;

    protected override describePersistedData(): string {
        return `${this.entries.length} ${this.describePersistedKeys()}`;
    }

    protected buildPayload(): object {
        return {values: this.entries};
    }
}

/**
 * Persists multiple value parameters of the same type in one request
 * (PATCH {@link RevaApi.PATH_VALUATIONS}/{id}[/segment] with a `{ values: [...] }` payload).
 * DOC_PROPERTY is excluded — it is handled by {@link PersistMultipleDocumentPropertiesApiInterface}.
 */
export class PersistMultipleParametersApiInterface extends AbstractPersistMultipleEntriesApiInterface {
    // biome-ignore lint/complexity/noUselessConstructor: narrows parameterType and entries, and re-exposes the protected base constructor
    constructor(
        parameterType: Exclude<PersistableParameterType, "DOC_PROPERTY">,
        id: string | null,
        parameters: ReadonlyArray<PersistParameterEntry>,
    ) {
        super(parameterType, id, parameters);
    }

    protected override describePersistedKeys(): string {
        return "parameters";
    }
}

/**
 * Replaces the entire set of parameters of a type in one request, instead of merging individual
 * entries into it (PUT {@link RevaApi.PATH_VALUATIONS}/{id}/segment with a `{ parameters: { ... } }`
 * envelope of parameter → value). Used for types whose layer is only ever replaced wholesale
 * (currently IMMODROP, #19159).
 */
export class PersistFullReplaceParametersApiInterface extends AbstractPersistApiInterface {
    constructor(
        parameterType: PersistableParameterType,
        id: string | null,
        private readonly parameters: Record<string, unknown>,
    ) {
        super(parameterType, id);
    }

    protected override describePersistedData(): string {
        return `${Object.keys(this.parameters).length} parameters`;
    }

    protected buildPayload(): object {
        return {parameters: this.parameters};
    }

    protected override async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPUT(url, credentials, this.buildPayload());
    }
}

/**
 * Persists multiple document properties of a valuation in one request
 * (PATCH {@link RevaApi.PATH_VALUATIONS}/{id}/properties with a `{ values: [...] }` payload).
 */
export class PersistMultipleDocumentPropertiesApiInterface extends AbstractPersistMultipleEntriesApiInterface {
    constructor(id: string | null, properties: ReadonlyArray<PersistDocumentPropertyEntry>) {
        super("DOC_PROPERTY", id, properties);
    }

    protected override describePersistedKeys(): string {
        return "properties";
    }
}
